import * as d3 from 'd3-selection'
import * as array from 'd3-array'
import linePoints from 'line-points'
import labels from './labels'
import percentages from './percentages'
import intersection from 'path-intersection'

const layout = {
  topCX: .165, // 16.5% of the width
  innerDelta: .95 // inner top ellipse is 95% of the outer
}

const ellipse = (cx, cy, rx, ry) => {
  return `M${cx - rx},${cy} a${rx},${ry} 0 1,0 ${rx * 2},0 a${rx},${ry} 0 1,0 ${-rx * 2},0`
}

/*
 * Returns the path points that make up the left outer arc of the outline
 */
const arcPoints = outline => {
  return linePoints(outline.splice(0, 2).join(' '))
}

/*
 * Paths are already shaped on the stage, this just draw them
 */
const stage = (selection, chartId) => {
  selection.each(function (d) {
    let group = d3.select(this)
      , path = group.selectAll('path').data(d => d.paths)
      , defs = d3.select(this.ownerSVGElement)
                 .selectAll('defs')
      , clip = defs.selectAll(`clipPath#${chartId}-stage-${d.idx}-${d._range.id}-clip`)
                   .data([d])
      , clipEnter = clip.enter()
                        .append('clipPath')

    clipEnter.append('path')

    clip.exit().remove()

    clip = clipEnter.merge(clip)
                    .attr('id', `${chartId}-stage-${d.idx}-${d._range.id}-clip`)

    group.attr('class', d => `stage is-deemphasizable ${d.color}`)
         .attr('clip-path', d => `url(#${chartId}-stage-${d.idx}-${d._range.id}-clip)`)
         .classed('deemphasize', d => d.deemphasize)
         .classed('is-clickable', d => d.clickable)

    path.exit().remove()

    path.enter()
        .append('path')
        .merge(path)
        .attr('d', d => d.d)
        .attr('fill', d => `url(#${d.fill})`)
        .attr('clip-path', d => d.clipPath)
        .attr('class', d => d.clazz)

    clip.select('path')
        .attr('d', d => {
          // Determine the bounding box of the main shape
          let bb = group.node().getBBox()
            , bottom = (d.nextTop || (bb.y + bb.height)) + 14 // TODO 14 matches the bottom radius of percentages
            , path = `M ${bb.x}, ${bb.y}
                      h ${bb.width}
                      V ${bottom}
                      h ${-bb.width}
                      V ${bb.y}
                      `

          // The default clip path allows us to show the entire shape
          if (d.intersectedPoints.length) {
            // Draw the intersected path as part of the clip
            path += `M${d.intersectedPoints[0]} ${d.intersectedPoints.slice(1).join('L')}`
          }
          return path
        })
  })
}

export default (selection, chart, funnels, outline, config) => {
  selection.each(function (d) {
    let container = d3.select(this)
                      .selectAll('g.funnel-container')
                      .data(funnels)

    container.exit().remove()

    container = container.enter()
                         .append('g')
                         .attr('class', 'funnel-container')
                         .merge(container)
                         .attr('transform', d => {
                           let xOffset = 0

                            if (d.dimensions.width < d.block.dimensions.width) {
                              xOffset = (d.block.dimensions.width - d.dimensions.width) / 2
                            }
                           return `translate(${d.block.dimensions.x + xOffset}, ${d.block.dimensions.y + d.dimensions.topPadding} )`
                         })

    let arcPts = arcPoints(outline)
      , group = container.selectAll('g.stage').data(d => {
        let points = d.points.slice().reverse()

        return points.map((p, idx) => {
          let dimensions = d.dimensions
            , paths = []
            , next = idx == points.length - 1 ? {height: 0, width: dimensions.width, offset: 0} : points[idx + 1]
            , bisector = array.bisector(d => d[1]) // y positions
            , topIdx = bisector.left(arcPts, next.height + p.offset)
            , bottomIdx = bisector.left(arcPts, p.height + p.offset)

          if (topIdx == bottomIdx) {
            // This wouldn't have height, so fake a height
            topIdx = bottomIdx - 1
          }

          let leftArc = arcPts.slice(topIdx, bottomIdx)
            , rightArc = leftArc.map(point => [dimensions.width - point[0], point[1]])
            , topWidth = rightArc[0][0] - leftArc[0][0]
            , topRx = topWidth / 2
            , topRy = (topWidth * layout.topCX) / 2
            , bottomWidth = rightArc[rightArc.length - 1][0] - leftArc[leftArc.length - 1][0]
            , bottomRx = bottomWidth / 2
            , bottomRy = bottomWidth * layout.topCX / 2
            , top = idx == d.points.length - 1
            , clipPath = `url(#${chart.id}-funnel-clip)`

          // Base shape
          let base = {
            d: `M ${leftArc[0]}
                ${leftArc.slice(1).join(' L')}
                a${bottomRx},${bottomRy} 0 1,0 ${bottomRx * 2},0
                M ${rightArc[rightArc.length - 1]}
                L ${rightArc.reverse().join(' L ')}
                L ${leftArc[0]}
               `,
            clipPath
          }

          // We want two base shapes, each uses a different gradient, so the
          // gradients overlap
          paths.push(Object.assign({}, base, {
            fill: `funnel-base-${p.color}-a`,
            clazz: `not-top`
          }))
          paths.push(Object.assign({}, base, {
            fill: `funnel-base-${p.color}-b`,
          }))

          // Outer top
          paths.push({
            d: ellipse(dimensions.center, leftArc[0][1], topRx, topRy),
            fill: `funnel-top-outer-${p.color}`,
            clipPath: !top ? clipPath : ''
          })

          // Inner top
          let topInnerPath = {
            d: ellipse(dimensions.center, leftArc[0][1], topRx * layout.innerDelta, topRy * layout.innerDelta),
            clipPath: !top ? clipPath : ''
          }
          paths.push(Object.assign({}, topInnerPath, {
            fill: `funnel-top-inner-${p.color}-a`,
            clazz: `not-top`
          }))
          paths.push(Object.assign({}, topInnerPath, {
            fill: `funnel-top-inner-${p.color}-b`,
          }))

          // Used for percentages
          points[idx].top = topRy + leftArc[0][1]

          // Find the inner bottom ellipsis. We need it to find the intersection
          // it forms w/ the top inner ellipsis. Used for clipping
          let bottomLeft = leftArc[leftArc.length - 1]
            , bottomInner = ellipse(dimensions.center, bottomLeft[1], bottomRx * layout.innerDelta, bottomRy * layout.innerDelta) // Used for clip
            , intersected = intersection(bottomInner, topInnerPath.d)
            , intersectedPoints = []

          if (intersected.length) {
            // The top inner ellipsis intersects w/ the bottom inner. Determine
            // Find all points of the bottom inner ellipsis that are above the intersection
            let topPoints = linePoints(bottomInner, 1).filter(point => point[1] < intersected[0].y)

            topPoints.sort((a, b) => b[0] - a[0])

            let bottomPoints = topPoints.map(point => {
              let intersect = intersection(topInnerPath.d, `M${point} v ${dimensions.height}`)
              if (intersect.length) {
                return [point[0], intersect[0].y]
              }
            }).filter(Boolean)

            bottomPoints.reverse() // So points go are continuous clockwise

            intersectedPoints = topPoints.concat(bottomPoints)
          }

          return {
            _point: p, // original point
            _range: d.block.range,
            deemphasize: p.deemphasize,
            clickable: p.clickable,
            idx,
            dimensions,
            paths,
            bottomWidth,
            topWidth,
            top: topRy + leftArc[0][1],
            bottom: leftArc[leftArc.length - 1][1] + bottomRy,
            nextTop: idx == 0 ? null : points[idx - 1].top,
            title: p.title,
            raw: p.raw,
            rawPercent: idx == 0 ? null : (points[idx - 1].raw / p.raw * 100) || 0,
            color: p.color,
            intersectedPoints,
            formatted: p.formatter(p.raw)
          }
        })
      })

    group.exit().remove()

    group = group.enter()
                 .append('g')
                 .merge(group)
                 .on('click', (e, d) => {
                   if (d.clickable) {
                     chart.ee.emit('chart-event', 'select:point', d._point, d._range)
                   }
                 })
    group.call(stage, chart.id)
         .call(labels, config)
         .call(percentages, config)
  })
}

export { layout }
